import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * THE GROUPED SHEET — the shared grammar behind every pillar → branch → topic
 * surface in the product.
 *
 * 🔴 WHY A SHEET AND NOT A FLOWCHART, AND WHY THIS IS SHARED.
 *
 * Two surfaces render the same editorial hierarchy: the client's Strategy pane
 * (`DeskStrategy`) and the operator's editorial Map (`EditorialPillarSheet`).
 * The Map used to draw it as a node-and-edge canvas with curved leader lines —
 * a single Pillar node beside ~2,000px of empty white, six edges running down
 * past nothing, and every topic's status rendered as identical grey text.
 * Founder, 2026-09-08: _"that white pillar chart with all the leader lines …
 * looks really terrible."_
 *
 * A flowchart earns its edges when the columns are STAGES and the question is
 * "where does this stop". That was true when the chain had five columns
 * (Pillar · Branch · Topic · Client review · Article). It is not true now: the
 * chain was reduced to three, all of them levels of one hierarchy, and a
 * hierarchy drawn as a flowchart is just an indented list that has paid for a
 * canvas. The sheet says the same thing in a fifth of the height, keeps every
 * number in a column you can compare down, and has room for a status.
 *
 * These primitives live here rather than in either component so the two cannot
 * drift — grammar duplicated across two surfaces is the #1 design smell in this
 * repo, and this is the exact pair the position doc named.
 */

/**
 * THE SHARED GRID. Every row at every depth uses this SAME template —
 * header, group, sub-group, leaf, footer — including a leading spacer column
 * that never carries content on the header or footer row.
 *
 * 🔴 THE BUG THIS EXISTS TO PREVENT. In the reference prototype the header
 * and footer rows each had one FEWER cell than the body rows (no leading
 * spacer), so every one of their cells sat one column left of its intended
 * label and the footer wrapped one word per line. A single constant, applied
 * unconditionally, makes that column-count mismatch impossible to reintroduce
 * by hand — there is no second definition to drift from this one.
 *
 * Below 720px the last two numeric columns fold away (see `HIDE_NARROW`) and
 * the grid narrows to three tracks, because three fixed numeric columns do not
 * fit a phone and the first is the one a reader actually needs.
 *
 * 🔴 `w-full` IS LOAD-BEARING. A `<button>` is shrink-to-fit, so on every row
 * rendered as a button — which is every row that opens a group — the grid box
 * sized to its CONTENT and `minmax(0,1fr)` collapsed to max-content. Measured
 * live on the real ITC desk: the pillar rows resolved to
 * `20px 199.484px 60px 60px 54px`, a different name-column width per row, so
 * the numeric columns landed at a different x on every line (21% at x=492,
 * 5% at x=485). The cells were `text-right` the whole time and it looked like
 * nothing was aligned — founder, twice: _"the numbers are weird and not
 * right-aligned like I already told you."_ It was never the alignment; it was
 * the track. Fixed here rather than at each call site so no consumer can
 * forget it again.
 */
export const SHEET_ROW =
  'grid w-full grid-cols-[20px_minmax(0,1fr)_60px_60px_54px] items-center gap-x-2.5 max-[720px]:grid-cols-[18px_minmax(0,1fr)_46px]';

/**
 * 🔴 HIDE BY AN EXPLICIT MARKER, NEVER BY COUNTING SIBLINGS. The reference
 * prototype's mobile stylesheet once hid columns with `.n:nth-of-type(2)`,
 * which counts every sibling `<span>` — the caret/status cell and the name
 * cell are spans too — so it hid the wrong column on three different row
 * shapes. This class is applied directly and only to the cells that should
 * fold away; nothing here infers a column from its position.
 */
export const HIDE_NARROW = 'max-[720px]:hidden';

export const NUMERIC_CELL = 'text-right text-caption tabular-nums text-muted-foreground';

/**
 * Carries the eye from a name to its numbers without moving the number off
 * its column. A flexible dotted rule, not literal "....." characters, so it
 * never wraps and is invisible to a screen reader.
 */
export function Leader(): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="mx-2 h-0 min-w-[10px] flex-1 translate-y-[-3px] border-b border-dotted border-border/70"
    />
  );
}

/**
 * The caret that opens a group. Rotation only — no swap between two glyphs,
 * which is what makes the open/closed transition read as one object moving
 * rather than two icons trading places.
 */
export function SheetCaret({ open }: { open: boolean }): React.JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className={cn(
        'size-3.5 shrink-0 text-muted-foreground transition-transform duration-150',
        open && 'rotate-90',
      )}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 3.5L10.5 8L6 12.5" />
    </svg>
  );
}
